import type { ReactElement } from 'react';
import { useStatsByDiscipline, type DisciplineStats } from '@/lib/stats';

/**
 * Grid de cards por disciplina — vista "Strava 30 días" (M2.12).
 *
 * 4 cards principales (swim/bike/run/strength) + brick/other como extras
 * si tienen datos. Por defecto siempre mostramos las 4 principales aunque
 * estén a 0 (un triatleta necesita ver "0 nataciones" para saber que falta).
 *
 * Cada card:
 *  - Emoji + nombre.
 *  - Big number: nº sesiones.
 *  - Horas totales.
 *  - Distancia total (si > 0).
 *  - Comparativa vs periodo anterior (badge ratio %).
 *  - Indicador de salud (🟢 alta, 🟡 normal, 🔴 baja, ⚪ sin datos).
 */

const PRIMARY_DISCIPLINES = ['swim', 'bike', 'run', 'strength'] as const;

const DISCIPLINE_META: Record<string, {
  label: string;
  emoji: string;
  /** Tailwind classes para el accent del card. */
  accent: string;
  /** Tailwind classes para el header bar coloreado. */
  bar: string;
}> = {
  swim: { label: 'Natación', emoji: '🏊', accent: 'text-cyan-700', bar: 'bg-cyan-600' },
  bike: { label: 'Bici', emoji: '🚴', accent: 'text-orange-700', bar: 'bg-orange-600' },
  run: { label: 'Carrera', emoji: '🏃', accent: 'text-emerald-700', bar: 'bg-emerald-600' },
  strength: { label: 'Fuerza', emoji: '💪', accent: 'text-slate-700', bar: 'bg-slate-700' },
  brick: { label: 'Brick', emoji: '🔁', accent: 'text-purple-700', bar: 'bg-purple-600' },
  other: { label: 'Otro', emoji: '⚡', accent: 'text-slate-700', bar: 'bg-slate-500' },
};

export function DisciplineStatsGrid({ days = 30 }: { days?: number }): ReactElement {
  const query = useStatsByDiscipline(days);

  return (
    <section
      aria-label={`Resumen por disciplina últimos ${days} días`}
      className="space-y-3"
    >
      <header className="flex items-baseline justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Por disciplina — últimos {days} días
        </h2>
      </header>

      {query.isLoading && (
        <p role="status" aria-live="polite" className="text-sm text-slate-600">
          Cargando estadísticas…
        </p>
      )}
      {query.isError && (
        <p role="alert" className="rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-800">
          No se pudo cargar el resumen por disciplina.
        </p>
      )}
      {query.data && (
        <div
          className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
          data-testid="discipline-stats-grid"
        >
          {PRIMARY_DISCIPLINES.map((d) => {
            const stat = query.data.disciplines.find((x) => x.discipline === d);
            if (!stat) return <DisciplineCard key={d} stat={emptyStat(d)} />;
            return <DisciplineCard key={d} stat={stat} />;
          })}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

function DisciplineCard({ stat }: { stat: DisciplineStats }): ReactElement {
  const meta = DISCIPLINE_META[stat.discipline] ?? DISCIPLINE_META.other!;
  const ratioPct = stat.prev30dRatio !== null
    ? Math.round((stat.prev30dRatio - 1) * 100)
    : null;
  const status = statusMeta(stat.status);

  return (
    <article
      aria-label={`${meta.label}: ${stat.sessions} sesiones, ${stat.hours} horas`}
      className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm"
    >
      <div className={`h-1 ${meta.bar}`} aria-hidden="true" />
      <div className="p-4">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span aria-hidden="true" className="text-xl">{meta.emoji}</span>
            <h3 className={`text-sm font-semibold ${meta.accent}`}>{meta.label}</h3>
          </div>
          <span
            title={status.hint}
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${status.className}`}
            data-testid={`discipline-status-${stat.discipline}`}
          >
            <span aria-hidden="true">{status.emoji}</span>
            {status.label}
          </span>
        </header>

        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-bold tabular-nums text-slate-900">
            {stat.sessions}
          </span>
          <span className="text-xs text-slate-600">
            {stat.sessions === 1 ? 'sesión' : 'sesiones'}
          </span>
        </div>

        <dl className="mt-2 space-y-0.5 text-xs">
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-500">Horas</dt>
            <dd className="font-medium text-slate-900">{formatHours(stat.hours)}</dd>
          </div>
          {stat.distanceKm > 0 && (
            <div className="flex items-baseline justify-between">
              <dt className="text-slate-500">Distancia</dt>
              <dd className="font-medium text-slate-900">
                {formatDistance(stat.distanceKm)}
              </dd>
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <dt className="text-slate-500">vs anterior</dt>
            <dd
              className={`font-medium ${
                ratioPct === null
                  ? 'text-slate-400'
                  : ratioPct >= 0
                    ? 'text-emerald-700'
                    : 'text-rose-700'
              }`}
            >
              {ratioPct === null
                ? '—'
                : `${ratioPct >= 0 ? '+' : ''}${ratioPct}%`}
            </dd>
          </div>
        </dl>
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function emptyStat(discipline: string): DisciplineStats {
  return {
    discipline,
    sessions: 0,
    hours: 0,
    distanceKm: 0,
    prevSessions: 0,
    prevHours: 0,
    prev30dRatio: null,
    status: 'none',
  };
}

function statusMeta(s: DisciplineStats['status']): {
  label: string;
  emoji: string;
  className: string;
  hint: string;
} {
  switch (s) {
    case 'high':
      return {
        label: 'alta',
        emoji: '🟢',
        className: 'bg-emerald-50 text-emerald-800',
        hint: 'volumen por encima del periodo anterior',
      };
    case 'normal':
      return {
        label: 'normal',
        emoji: '🟢',
        className: 'bg-teal-50 text-teal-800',
        hint: 'volumen similar al periodo anterior',
      };
    case 'low':
      return {
        label: 'baja',
        emoji: '🟡',
        className: 'bg-amber-50 text-amber-800',
        hint: 'volumen por debajo del periodo anterior',
      };
    case 'none':
      return {
        label: 'sin datos',
        emoji: '⚪',
        className: 'bg-slate-100 text-slate-600',
        hint: 'no hay actividades en este periodo',
      };
  }
}

function formatHours(h: number): string {
  if (!Number.isFinite(h) || h <= 0) return '—';
  if (Number.isInteger(h)) return `${h} h`;
  return `${h.toFixed(1)} h`;
}

function formatDistance(km: number): string {
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}
